import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { TrendingUp, Target, CheckCircle, AlertCircle } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { useLeads } from "@/hooks/useLeads";
import { getMonthlyIncomeSummary, getMonthString } from "@/services/incomeService";
import type { MonthlyIncomeSummary } from "@/types/income";

function formatCurrency(amount: number) {
  if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
  if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
  return `₹${amount.toLocaleString("en-IN")}`;
}

export function IncentiveWidget() {
  const { user } = useAuth();
  const { leads } = useLeads();
  const [incomeSummary, setIncomeSummary] = useState<MonthlyIncomeSummary | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Calculate real incentives from current month's income summary
  const currentMonth = getMonthString(new Date());
  
  // For salaried users: Show salary progress first, then incentive
  // For freelance users: Show total income as incentive
  const isSalaried = incomeSummary?.employmentType === 'salaried';
  const monthlySalary = incomeSummary?.monthlySalary || 0;
  const totalRevenue = incomeSummary?.totalRevenueGenerated || 0;
  const totalIncome = incomeSummary?.totalIncome || 0;
  
  let displayAmount = 0;
  let displayTarget = 0;
  let displayLabel = "";
  let showProgressBar = false;
  let isSalaryJustified = false;
  
  if (isSalaried && monthlySalary > 0) {
    // For salaried: Check if salary is justified
    isSalaryJustified = totalRevenue >= monthlySalary;
    showProgressBar = true; // Always show progress for salaried
    
    if (isSalaryJustified) {
      // Salary reached! Show incentive (amount above salary)
      displayAmount = Math.max(0, totalIncome - monthlySalary);
      displayTarget = totalIncome; // Show total income as target
      displayLabel = "Incentives Earned";
    } else {
      // Still working towards salary
      displayAmount = totalRevenue;
      displayTarget = monthlySalary;
      displayLabel = "Salary Progress";
    }
  } else {
    // Freelance: Show total income as incentive, NO progress bar
    displayAmount = totalIncome;
    displayTarget = 0;
    displayLabel = "Incentives Earned";
    showProgressBar = false;
  }
  
  const progress = displayTarget > 0 ? (displayAmount / displayTarget) * 100 : 0;

  useEffect(() => {
    const loadIncomeSummary = async () => {
      if (!user?.phone) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const summary = await getMonthlyIncomeSummary(user.phone, currentMonth);
        setIncomeSummary(summary);
      } catch (error) {
        // Silently fail - income data may not be available yet
        console.debug('Income summary not available:', error);
        setIncomeSummary(null);
      } finally {
        setLoading(false);
      }
    };
    
    loadIncomeSummary();
  }, [user?.phone, currentMonth]);

  return (
    <Card variant="gold" className="relative overflow-hidden w-full min-w-0">
      <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full bg-gold/10 blur-3xl" />
      <CardContent className="relative p-3 md:p-5 space-y-3 md:space-y-4">
        {/* Incentive earned or Salary Progress */}
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs md:text-sm text-muted-foreground">{displayLabel}</p>
            <p className="text-xl md:text-3xl font-bold text-gold">
              {loading ? '...' : formatCurrency(displayAmount)}
            </p>
            
            {/* Salary justification indicator - only for salaried users */}
            {!loading && isSalaried && monthlySalary > 0 && (
              <div className="flex items-center gap-1 mt-1">
                {isSalaryJustified ? (
                  <>
                    <CheckCircle className="h-3 w-3 text-green-500" />
                    <span className="text-[10px] text-green-600">
                      ✓ Salary justified! Earning bonus
                    </span>
                  </>
                ) : (
                  <>
                    <AlertCircle className="h-3 w-3 text-amber-500" />
                    <span className="text-[10px] text-amber-600">
                      {formatCurrency(monthlySalary - totalRevenue)} more to reach salary
                    </span>
                  </>
                )}
              </div>
            )}
          </div>
          <div className="flex h-9 w-9 md:h-12 md:w-12 items-center justify-center rounded-xl bg-gold/20">
            <TrendingUp className="h-4 w-4 md:h-6 md:w-6 text-gold" />
          </div>
        </div>

        {/* Progress bar - Only for salaried users */}
        {showProgressBar && (
          <div className="space-y-1.5 md:space-y-2">
            <div className="flex justify-between text-xs md:text-sm">
              <span className="text-muted-foreground">
                {isSalaried && monthlySalary > 0 && !isSalaryJustified 
                  ? "Salary Target" 
                  : "Monthly Performance"}
              </span>
              <span className="font-medium">{Math.min(progress, 100).toFixed(0)}%</span>
            </div>
            <div className="h-2 md:h-3 rounded-full bg-secondary overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${Math.min(progress, 100)}%` }}
                transition={{ duration: 1, ease: "easeOut" }}
                className="h-full rounded-full bg-gradient-to-r from-gold to-warning"
              />
            </div>
            <div className="flex justify-between text-[10px] md:text-xs text-muted-foreground">
              <span>{formatCurrency(displayAmount)}</span>
              <span>{formatCurrency(displayTarget)}</span>
            </div>
          </div>
        )}

        {/* Target badge */}
        <div className="flex items-center gap-2 rounded-lg bg-gold/10 p-2 md:p-3 border border-gold/20">
          <Target className="h-3 w-3 md:h-4 md:w-4 text-gold flex-shrink-0" />
          <p className="text-[10px] md:text-xs">
            {isSalaried && monthlySalary > 0 && !isSalaryJustified ? (
              <>
                <span className="font-medium text-gold">Keep pushing!</span>
                {" "}Reach your salary target to start earning incentives.
              </>
            ) : (
              <>
                <span className="font-medium text-gold">Great work!</span>
                {" "}{isSalaried && isSalaryJustified 
                  ? "You've crossed your salary threshold!" 
                  : "You're making great progress this month."}
              </>
            )}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}